import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material"
import { SelectChangeEvent } from "@mui/material/Select"
import React, { useCallback, useContext, useEffect, useId, useMemo, useState } from "react"
import { doc, getDoc } from "firebase/firestore"

import { db } from "./firebase"
import { getCountryInfo } from "./utils"
import { motion } from "framer-motion"
import { useAppConfiguration } from "./components/AppConfigurationService"
import { useTranslation } from "react-i18next"
import { LocationContext } from "react-ui-components/components/location/LocationSelector"
import { ILocation, ILocationContext, ILocationItem } from "react-ui-components/components/models/ILocation"

type LocationField = "country" | "state" | "city"

interface LocationSelectorProps {
  onChange?: (field: LocationField, value: string) => void
  userSelectedCountry?: string
  userSelectedState?: string
  userSelectedCity?: string
  disabled?: boolean
  className?: string
}

interface SimpleLocationSelectorProps {
  value?: string
  onChange?: (field: LocationField, value: string) => void
  label?: string
  disabled?: boolean
}

const DEFAULT_COUNTRY = "US"

/**
 * Fetches available locations and provides a country/state/city selector UI.
 */
const LocationSelector = ({
  onChange,
  userSelectedCountry,
  userSelectedState,
  userSelectedCity,
  disabled,
  className,
}: LocationSelectorProps) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [locations, setLocations] = useState<ILocation>({})
  const [selectedCountry, setSelectedCountry] = useState(userSelectedCountry || DEFAULT_COUNTRY)
  const [selectedState, setSelectedState] = useState(userSelectedState || "")
  const [selectedCity, setSelectedCity] = useState(userSelectedCity || "")
  const countryLabelId = useId()
  const stateLabelId = useId()
  const cityLabelId = useId()

  const countryOptions = useMemo(() => Object.keys(locations || {}).sort(), [locations])
  const stateOptions = useMemo(() => {
    if (!selectedCountry) {
      return []
    }
    return Object.keys(locations?.[selectedCountry] || {}).sort()
  }, [locations, selectedCountry])
  const cityOptions = useMemo(() => {
    if (!selectedCountry || !selectedState) {
      return []
    }
    return [...(locations?.[selectedCountry]?.[selectedState] || [])].sort()
  }, [locations, selectedCountry, selectedState])

  const fetchLocations = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const docRef = doc(db, "app-configurations/availableLocations")
      const docSnap = await getDoc(docRef)
      if (docSnap.exists()) {
        const data = docSnap.data() as ILocation
        setLocations(data || {})
      } else {
        setLocations({})
      }
    } catch (error) {
      console.error("Error fetching locations:", error)
      setError(
        error instanceof Error
          ? error.message
          : t("Unable to load locations. Please try again."),
      )
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    fetchLocations()
  }, [fetchLocations])

  useEffect(() => {
    setSelectedCountry(userSelectedCountry || DEFAULT_COUNTRY)
  }, [userSelectedCountry])

  useEffect(() => {
    setSelectedState(userSelectedState || "")
  }, [userSelectedState])

  useEffect(() => {
    setSelectedCity(userSelectedCity || "")
  }, [userSelectedCity])

  useEffect(() => {
    if (selectedState && !stateOptions.includes(selectedState)) {
      setSelectedState("")
      setSelectedCity("")
    }
  }, [selectedState, stateOptions])

  useEffect(() => {
    if (selectedCity && !cityOptions.includes(selectedCity)) {
      setSelectedCity("")
    }
  }, [selectedCity, cityOptions])

  useEffect(() => {
    if (countryOptions.length > 0 && selectedCountry && !countryOptions.includes(selectedCountry)) {
      setSelectedCountry("")
      setSelectedState("")
      setSelectedCity("")
    }
  }, [countryOptions, selectedCountry])

  const handleCountryChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const country = event.target.value
      setSelectedCountry(country)
      setSelectedState("")
      setSelectedCity("")
      onChange?.("country", country)
    },
    [onChange],
  )

  const handleStateChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const state = event.target.value
      setSelectedState(state)
      setSelectedCity("")
      onChange?.("state", state)
    },
    [onChange],
  )

  const handleCityChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const city = event.target.value
      setSelectedCity(city)
      onChange?.("city", city)
    },
    [onChange],
  )

  const hasLocations = countryOptions.length > 0
  const isDisabled = Boolean(disabled || loading || (error && !hasLocations))
  const isEmpty = !loading && !error && !hasLocations
  const rootClassName = className ? `p-4 ${className}` : "p-4"

  return (
    <div className={rootClassName}>
      <Stack spacing={2}>
        {error ? (
          <Alert
            severity="error"
            variant="outlined"
            action={
              <Button color="inherit" size="small" onClick={fetchLocations} disabled={loading}>
                {t("Retry")}
              </Button>
            }
          >
            {error}
          </Alert>
        ) : null}
        {isEmpty ? (
          <Alert severity="info" variant="outlined">
            {t("No locations available.")}
          </Alert>
        ) : null}
        {loading ? (
          <Box className="flex items-center gap-3">
            <CircularProgress size={20} />
            <Typography variant="body2">{t("Loading locations...")}</Typography>
          </Box>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Stack spacing={2}>
              <FormControl fullWidth disabled={isDisabled} variant="outlined">
                <InputLabel id={countryLabelId} shrink>
                  {t("Select Country")}
                </InputLabel>
                <Select
                  value={selectedCountry}
                  label={t("Select Country")}
                  labelId={countryLabelId}
                  id={`${countryLabelId}-select`}
                  onChange={handleCountryChange}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) {
                      return <span>{t("Select Country")}</span>
                    }
                    const countryCode = String(value)
                    const countryInfo = getCountryInfo(countryCode)
                    return (
                      <div className="flex items-center gap-1">
                        <span>{countryInfo?.emoji || "🌍"}</span>
                        <span>{t(countryInfo?.name || countryCode)}</span>
                      </div>
                    )
                  }}
                >
                  {countryOptions.length === 0 ? (
                    <MenuItem value="" disabled>
                      {t("No countries available")}
                    </MenuItem>
                  ) : (
                    countryOptions.map((country) => {
                      const countryInfo = getCountryInfo(country)
                      return (
                        <MenuItem key={country} value={country}>
                          <div className="flex items-center gap-1">
                            <span>{countryInfo?.emoji || "🌍"}</span>
                            <span>{t(countryInfo?.name || country)}</span>
                          </div>
                        </MenuItem>
                      )
                    })
                  )}
                </Select>
                {!countryOptions.length && !loading ? (
                  <FormHelperText>{t("No countries configured")}</FormHelperText>
                ) : null}
              </FormControl>

              <FormControl fullWidth disabled={isDisabled || !selectedCountry} variant="outlined">
                <InputLabel id={stateLabelId} shrink>
                  {t("Select State")}
                </InputLabel>
                <Select
                  value={selectedState}
                  label={t("Select State")}
                  labelId={stateLabelId}
                  id={`${stateLabelId}-select`}
                  onChange={handleStateChange}
                  displayEmpty
                >
                  <MenuItem value="">{t("Select State")}</MenuItem>
                  {stateOptions.length === 0 ? (
                    <MenuItem value="" disabled>
                      {t("No states available")}
                    </MenuItem>
                  ) : (
                    stateOptions.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {!selectedCountry ? (
                  <FormHelperText>{t("Select a country first")}</FormHelperText>
                ) : stateOptions.length === 0 ? (
                  <FormHelperText>{t("No states available")}</FormHelperText>
                ) : null}
              </FormControl>

              <FormControl fullWidth disabled={isDisabled || !selectedState} variant="outlined">
                <InputLabel id={cityLabelId} shrink>
                  {t("Select City")}
                </InputLabel>
                <Select
                  value={selectedCity}
                  label={t("Select City")}
                  labelId={cityLabelId}
                  id={`${cityLabelId}-select`}
                  onChange={handleCityChange}
                  displayEmpty
                >
                  <MenuItem value="">{t("Select City")}</MenuItem>
                  {cityOptions.length === 0 ? (
                    <MenuItem value="" disabled>
                      {t("No cities available")}
                    </MenuItem>
                  ) : (
                    cityOptions.map((city) => (
                      <MenuItem key={city} value={city}>
                        {city}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {!selectedState ? (
                  <FormHelperText>{t("Select a state first")}</FormHelperText>
                ) : cityOptions.length === 0 ? (
                  <FormHelperText>{t("No cities available")}</FormHelperText>
                ) : null}
              </FormControl>
            </Stack>
          </motion.div>
        )}
      </Stack>
    </div>
  )
}

export default LocationSelector

/**
 * Hook to access the shared location context.
 */
export const useLocation = () => {
  return useContext(LocationContext as any) as ILocationContext
}

interface LocationProviderProps {
  children: React.ReactNode
  formData?: Partial<ILocationItem> & {
    province?: string
    state?: string
    city?: string
    country?: string
  }
}

/**
 * Provides location context for Country/State/City selector widgets.
 */
export const LocationProvider = ({ children, formData }: LocationProviderProps) => {
  const [loading, setLoading] = useState(true)

  const [availableLocations, setAvailableLocations] = useState<ILocation>({})
  const [currentLocation, setCurrentLocation] = useState<ILocationItem>({
    country: formData?.country || "",
    city: formData?.city || "",
    state: formData?.province || formData?.state || "",
  })
  const { availableLocations: locations } = useAppConfiguration()

  useEffect(() => {
    setAvailableLocations(locations || {})
    setLoading(false)
  }, [locations])

  useEffect(() => {
    if (!formData) {
      return
    }
    setCurrentLocation((prev) => ({
      ...prev,
      country: formData?.country || "",
      city: formData?.city || "",
      state: formData?.province || formData?.state || "",
    }))
  }, [formData?.country, formData?.city, formData?.province, formData?.state])

  return (
    <LocationContext.Provider
      value={{
        currentLocation,
        availableLocations,
        setCurrentLocation,
        setAvailableLocations,
        loading,
      }}
    >
      {children as any}
    </LocationContext.Provider>
  )
}

/**
 * Country selector tied to the LocationProvider context.
 */
export const CountryLocationSelector = ({
  value,
  onChange,
  label,
  disabled,
}: SimpleLocationSelectorProps) => {
  const { t } = useTranslation()
  const { availableLocations, currentLocation, setCurrentLocation, loading }: ILocationContext =
    useLocation()
  const labelText = t(label || "Select Country")
  const countryLabelId = useId()

  const countryOptions = useMemo(
    () => Object.keys(availableLocations || {}).sort(),
    [availableLocations],
  )

  const updateLocation = useCallback(
    (nextCountry: string) => {
      const country = nextCountry || ""
      const states = country ? Object.keys(availableLocations?.[country] || {}).sort() : []
      setCurrentLocation?.((prev) => ({
        ...prev,
        country,
        state: "",
        city: "",
        states,
        cities: [],
      }))
    },
    [availableLocations, setCurrentLocation],
  )

  useEffect(() => {
    if (
      currentLocation?.country &&
      countryOptions.length > 0 &&
      !countryOptions.includes(currentLocation.country)
    ) {
      updateLocation("")
    }
  }, [countryOptions, currentLocation?.country, updateLocation])

  useEffect(() => {
    if (typeof value !== "undefined" && value !== currentLocation?.country) {
      updateLocation(value)
    }
  }, [currentLocation?.country, updateLocation, value])

  const handleCountryChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const nextCountry = event.target.value
      updateLocation(nextCountry)
      onChange?.("country", nextCountry)
    },
    [onChange, updateLocation],
  )

  const hasCountries = countryOptions.length > 0
  const isDisabled = Boolean(disabled || loading || !hasCountries)

  return (
    <div className="p-4">
      {loading ? (
        <Box className="flex items-center gap-3">
          <CircularProgress size={20} />
          <Typography variant="body2">{t("Loading countries...")}</Typography>
        </Box>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <FormControl fullWidth className="mb-4" disabled={isDisabled} variant="outlined">
            <InputLabel id={countryLabelId} shrink>
              {labelText}
            </InputLabel>
            <Select
              value={currentLocation.country || ""}
              label={labelText}
              labelId={countryLabelId}
              id={`${countryLabelId}-select`}
              onChange={handleCountryChange}
              displayEmpty
              renderValue={(currentValue) => {
                if (!currentValue) {
                  return <span>{labelText}</span>
                }
                const countryCode = String(currentValue)
                const info = getCountryInfo(countryCode)
                return (
                  <div className="flex items-center gap-1">
                    <span>{info?.emoji || "🌍"}</span>
                    <span>{t(info?.name || countryCode)}</span>
                  </div>
                )
              }}
            >
              {countryOptions.length === 0 ? (
                <MenuItem value="" disabled>
                  {t("No countries available")}
                </MenuItem>
              ) : (
                countryOptions.map((country) => {
                  const info = getCountryInfo(country)
                  return (
                    <MenuItem key={country} value={country}>
                      <div className="flex items-center gap-1">
                        <span>{info?.emoji || "🌍"}</span>
                        <span>{t(info?.name || country)}</span>
                      </div>
                    </MenuItem>
                  )
                })
              )}
            </Select>
            {!hasCountries && !loading ? (
              <FormHelperText>{t("No countries configured")}</FormHelperText>
            ) : null}
          </FormControl>
        </motion.div>
      )}
    </div>
  )
}

/**
 * State selector tied to the LocationProvider context.
 */
export const StateLocationSelector = ({
  value,
  onChange,
  label,
  disabled,
}: SimpleLocationSelectorProps) => {
  const { t } = useTranslation()
  const { availableLocations, currentLocation, setCurrentLocation, loading }: ILocationContext =
    useLocation()
  const labelText = t(label || "Select State")
  const stateLabelId = useId()

  const stateOptions = useMemo(() => {
    const country = currentLocation?.country || ""
    return country ? Object.keys(availableLocations?.[country] || {}).sort() : []
  }, [availableLocations, currentLocation?.country])

  const updateLocation = useCallback(
    (nextState: string) => {
      const state = nextState || ""
      const country = currentLocation?.country || ""
      const states = country ? Object.keys(availableLocations?.[country] || {}).sort() : []
      const cities = state ? [...(availableLocations?.[country]?.[state] || [])].sort() : []
      setCurrentLocation?.((prev) => ({
        ...prev,
        country,
        state,
        city: "",
        states,
        cities,
      }))
    },
    [availableLocations, currentLocation?.country, setCurrentLocation],
  )

  useEffect(() => {
    if (
      currentLocation?.state &&
      stateOptions.length > 0 &&
      !stateOptions.includes(currentLocation.state)
    ) {
      updateLocation("")
    }
  }, [currentLocation?.state, stateOptions, updateLocation])

  useEffect(() => {
    if (typeof value !== "undefined" && value !== currentLocation?.state) {
      updateLocation(value)
    }
  }, [currentLocation?.state, updateLocation, value])

  const handleChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const nextState = event.target.value
      updateLocation(nextState)
      onChange?.("state", nextState)
    },
    [onChange, updateLocation],
  )

  const hasCountry = Boolean(currentLocation?.country)
  const hasStates = stateOptions.length > 0
  const isDisabled = Boolean(disabled || loading || !hasCountry || !hasStates)

  return (
    <div className="p-4">
      {loading ? (
        <Box className="flex items-center gap-3">
          <CircularProgress size={20} />
          <Typography variant="body2">{t("Loading states...")}</Typography>
        </Box>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <FormControl fullWidth className="mb-4" disabled={isDisabled} variant="outlined">
            <InputLabel id={stateLabelId} shrink>
              {labelText}
            </InputLabel>
            <Select
              value={currentLocation.state || ""}
              label={labelText}
              labelId={stateLabelId}
              id={`${stateLabelId}-select`}
              onChange={handleChange}
              displayEmpty
              renderValue={(currentValue) =>
                currentValue ? (
                  <div className="flex items-center gap-1">
                    <span>{t(String(currentValue))}</span>
                  </div>
                ) : (
                  <span>{labelText}</span>
                )
              }
            >
              {stateOptions.length === 0 ? (
                <MenuItem value="" disabled>
                  {t("No states available")}
                </MenuItem>
              ) : (
                stateOptions.map((state) => (
                  <MenuItem key={state} value={state}>
                    <div className="flex items-center gap-1">
                      <span>{t(state)}</span>
                    </div>
                  </MenuItem>
                ))
              )}
            </Select>
            {!hasCountry ? (
              <FormHelperText>{t("Select a country first")}</FormHelperText>
            ) : !hasStates ? (
              <FormHelperText>{t("No states available")}</FormHelperText>
            ) : null}
          </FormControl>
        </motion.div>
      )}
    </div>
  )
}

/**
 * City selector tied to the LocationProvider context.
 */
export const CityLocationSelector = ({
  value,
  onChange,
  label,
  disabled,
}: SimpleLocationSelectorProps) => {
  const { t } = useTranslation()
  const { availableLocations, currentLocation, setCurrentLocation, loading }: ILocationContext =
    useLocation()
  const labelText = t(label || "Select City")
  const cityLabelId = useId()

  const cityOptions = useMemo(() => {
    const country = currentLocation?.country || ""
    const state = currentLocation?.state || ""
    return country && state ? [...(availableLocations?.[country]?.[state] || [])].sort() : []
  }, [availableLocations, currentLocation?.country, currentLocation?.state])

  const updateLocation = useCallback(
    (nextCity: string) => {
      const city = nextCity || ""
      setCurrentLocation?.((prev) => ({
        ...prev,
        city,
      }))
    },
    [setCurrentLocation],
  )

  useEffect(() => {
    if (
      currentLocation?.city &&
      cityOptions.length > 0 &&
      !cityOptions.includes(currentLocation.city)
    ) {
      updateLocation("")
    }
  }, [cityOptions, currentLocation?.city, updateLocation])

  useEffect(() => {
    if (typeof value !== "undefined" && value !== currentLocation?.city) {
      updateLocation(value)
    }
  }, [currentLocation?.city, updateLocation, value])

  const handleChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const nextCity = event.target.value
      updateLocation(nextCity)
      onChange?.("city", nextCity)
    },
    [onChange, updateLocation],
  )

  const hasState = Boolean(currentLocation?.state)
  const hasCities = cityOptions.length > 0
  const isDisabled = Boolean(disabled || loading || !hasState || !hasCities)

  return (
    <div className="p-4">
      {loading ? (
        <Box className="flex items-center gap-3">
          <CircularProgress size={20} />
          <Typography variant="body2">{t("Loading cities...")}</Typography>
        </Box>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <FormControl fullWidth className="mb-4" disabled={isDisabled} variant="outlined">
            <InputLabel id={cityLabelId} shrink>
              {labelText}
            </InputLabel>
            <Select
              value={currentLocation.city || ""}
              label={labelText}
              labelId={cityLabelId}
              id={`${cityLabelId}-select`}
              onChange={handleChange}
              displayEmpty
              renderValue={(currentValue) =>
                currentValue ? (
                  <div className="flex items-center gap-1">
                    <span>{t(String(currentValue))}</span>
                  </div>
                ) : (
                  <span>{labelText}</span>
                )
              }
            >
              {cityOptions.length === 0 ? (
                <MenuItem value="" disabled>
                  {t("No cities available")}
                </MenuItem>
              ) : (
                cityOptions.map((city) => (
                  <MenuItem key={city} value={city}>
                    <div className="flex items-center gap-1">
                      <span>{t(city)}</span>
                    </div>
                  </MenuItem>
                ))
              )}
            </Select>
            {!hasState ? (
              <FormHelperText>{t("Select a state first")}</FormHelperText>
            ) : !hasCities ? (
              <FormHelperText>{t("No cities available")}</FormHelperText>
            ) : null}
          </FormControl>
        </motion.div>
      )}
    </div>
  )
}
